/**
 * Discount code landing.
 *
 * When a visitor arrives via /discount/CODE, Shopify stores the code in a `discount_code` cookie whether or
 * not the code exists. This module asks Shopify whether the code is valid, then:
 *  - valid:   toast, replacement announcement bar messages, and a cart reminder
 *  - invalid: only an "invalid or expired" toast; the announcement bar and cart are untouched
 *  - unknown (the check failed): nothing, so we never claim anything we couldn't confirm
 * Every message comes from the same config (snippets/discount-landing.liquid), shares one {code}
 * replacement, and is inserted with textContent only.
 */

const CODE_KEY = 'dl:code';
const VALID_KEY = 'dl:valid:';
const TOAST_KEY = 'dl:toast:';
const VALID_TTL = 30 * 60 * 1000;
const API_VERSION = '2026-10';

/**
 * @typedef {object} Config
 * @property {boolean} replaceBar
 * @property {number} dismissSeconds
 * @property {string} closeLabel
 * @property {number | string} probeVariant
 * @property {string} fallbackOrigin
 * @property {string} toast
 * @property {string} cart
 * @property {string[]} bar
 * @property {string} invalid
 */

const storage = {
  /** @param {string} key */
  get(key) {
    try {
      return sessionStorage.getItem(key);
    } catch {
      return null;
    }
  },
  /**
   * @param {string} key
   * @param {string} value
   */
  set(key, value) {
    try {
      sessionStorage.setItem(key, value);
    } catch {
      // Storage unavailable; the cookie still drives later pages.
    }
  },
  clearAll() {
    try {
      Object.keys(sessionStorage)
        .filter((key) => key.startsWith('dl:'))
        .forEach((key) => sessionStorage.removeItem(key));
    } catch {
      // ignore
    }
  },
};

/** @returns {Config | null} */
function readConfig() {
  try {
    return JSON.parse(document.getElementById('discount-landing-config')?.textContent ?? '');
  } catch {
    return null;
  }
}

/**
 * Primary source is the `discount_code` cookie (set by Shopify without HttpOnly, so readable here).
 * Fallbacks: a `?discount=` style query param (e.g. a redirect like /discount/CODE?redirect=/%3Fdiscount%3DCODE)
 * and sessionStorage.
 */
function detectCode() {
  const params = new URLSearchParams(location.search);
  const fromUrl = params.get('discount') || params.get('discount_code') || params.get('dc');

  let fromCookie = '';
  const match = document.cookie.match(/(?:^|; )discount_code=([^;]*)/);
  if (match) {
    try {
      fromCookie = decodeURIComponent(match[1] ?? '');
    } catch {
      fromCookie = match[1] ?? '';
    }
  }

  return (fromUrl || fromCookie || storage.get(CODE_KEY) || '').trim();
}

/**
 * @param {string} template
 * @param {string} code
 */
const format = (template, code) => template.replace(/\{code\}/gi, () => code);

/**
 * Asks the Storefront API (no token needed) to apply the code to a throwaway cart and reports whether
 * Shopify accepted it. Expired, nonexistent and ineligible codes come back not applicable; per-customer
 * codes come back applicable. One shirt and three shirts, because a code with a minimum spend rejects a
 * single shirt. Not more than three: past the bundle tiers the automatic discount wins and Shopify reports
 * the code as not applicable.
 *
 * @param {string} code
 * @param {Config} config
 * @returns {Promise<boolean | null>} null when the check itself failed
 */
async function codeIsValid(code, config) {
  const merchandiseId = `gid://shopify/ProductVariant/${config.probeVariant}`;
  const query = `mutation($lines:[CartLineInput!],$codes:[String!]){cartCreate(input:{lines:$lines,discountCodes:$codes}){cart{discountCodes{applicable}}}}`;

  /**
   * @param {string} origin
   * @param {number} quantity
   * @returns {Promise<boolean | null>}
   */
  const probeAt = async (origin, quantity) => {
    const controller = new AbortController();
    const timeout = setTimeout(() => controller.abort(), 4000);
    try {
      const response = await fetch(`${origin}/api/${API_VERSION}/graphql.json`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ query, variables: { lines: [{ merchandiseId, quantity }], codes: [code] } }),
        signal: controller.signal,
      });
      if (!response.ok) {
        console.warn(`[discount-landing] code check failed at ${origin || location.origin}: HTTP ${response.status}`);
        return null;
      }

      const applied = (await response.json())?.data?.cartCreate?.cart?.discountCodes;
      return Array.isArray(applied) ? applied.some((/** @type {{ applicable: boolean }} */ d) => d.applicable) : null;
    } catch (error) {
      console.warn(`[discount-landing] code check failed at ${origin || location.origin}:`, error);
      return null;
    } finally {
      clearTimeout(timeout);
    }
  };

  // Same origin first. Some hosts (e.g. shopifypreview.com links) don't serve the Storefront API, so fall
  // back to the store's permanent domain, which does and allows cross-origin requests.
  /** @param {number} quantity */
  const probe = async (quantity) => {
    const result = await probeAt('', quantity);
    if (result !== null || !config.fallbackOrigin || config.fallbackOrigin === location.origin) return result;
    return probeAt(config.fallbackOrigin, quantity);
  };

  // Run both at once to keep the announcement bar hidden for as short a time as possible.
  const results = await Promise.all([probe(1), probe(3)]);
  if (results.includes(true)) return true;
  return results.includes(null) ? null : false;
}

/**
 * Cached per code for the session so only the first page pays for the check.
 *
 * @param {string} code
 * @param {Config} config
 * @returns {Promise<boolean | null>}
 */
async function checkCode(code, config) {
  const key = VALID_KEY + code.toLowerCase();

  try {
    const cached = JSON.parse(storage.get(key) ?? 'null');
    if (cached && Date.now() - cached.t < VALID_TTL) return cached.ok;
  } catch {
    // fall through to a fresh check
  }

  if (!config.probeVariant) {
    console.warn('[discount-landing] no purchasable product found to check the code against');
    return null;
  }

  const ok = await codeIsValid(code, config);
  if (ok !== null) storage.set(key, JSON.stringify({ ok, t: Date.now() }));
  return ok;
}

/* Cart notes (page and drawer). Idempotent so it can run after every re-render. */

/**
 * @param {string} message
 */
function renderCartNotes(message) {
  for (const note of document.querySelectorAll('[data-discount-landing-note]')) {
    // The checkout button is disabled when the cart is empty; don't show a reminder for nothing.
    const emptyCart = note.closest('.cart__summary-totals')?.parentElement?.querySelector('.cart__checkout-button[disabled]');
    const text = emptyCart ? '' : message;

    if (note.textContent !== text) note.textContent = text;
    if (note.hidden !== !text) note.hidden = !text;
  }
}

/**
 * @param {string} message
 */
function watchCart(message) {
  const run = () => renderCartNotes(message);

  // The theme morphs cart HTML in place on every cart change, which resets the note to its server-rendered
  // (hidden, empty) state. A MutationObserver callback runs before the next paint, so the note is restored
  // without a visible flicker. renderCartNotes only writes on change, so its own writes settle immediately.
  new MutationObserver(run).observe(document.body, { childList: true, subtree: true });
  document.addEventListener('cart:update', run);
  document.addEventListener('discount:update', run);
  run();
}

/* Announcement bar */

/**
 * @param {string[]} messages
 */
function replaceAnnouncementBar(messages) {
  const bar = document.querySelector('announcement-bar-component');
  const container = bar?.querySelector('.announcement-bar__slides');
  const template = container?.querySelector('.announcement-bar__slide');
  if (!bar || !container || !template || !messages.length) return;

  const slides = messages.map((message, index) => {
    const slide = /** @type {HTMLElement} */ (template.cloneNode(true));
    slide.querySelectorAll('countdown-loader, script').forEach((node) => node.remove());
    for (const name of slide.getAttributeNames()) {
      if (name.startsWith('data-shopify')) slide.removeAttribute(name);
    }
    slide.classList.add('dl-bar-slide');
    slide.setAttribute('aria-hidden', String(index !== 0));

    const text = slide.querySelector('.announcement-bar__text');
    if (text) text.textContent = message;
    return slide;
  });

  container.querySelectorAll('.announcement-bar__slide').forEach((slide) => slide.remove());
  container.append(...slides);
  bar.setAttribute('data-discount-landing', 'active');

  const arrows = bar.querySelector('slideshow-arrows');
  const rotate = slides.length > 1;
  arrows?.classList.toggle('dl-arrows-hidden', !rotate);

  // Reuse the bar's own autoplay/rotation. The section only loads announcement-bar.js and sets
  // `autoplay` when it has 2+ blocks, so start it here when the default bar was a single message.
  const speed = bar.getAttribute('autoplay') || bar.getAttribute('data-autoplay-speed');
  customElements.whenDefined('announcement-bar-component').then(() => {
    const component = /** @type {any} */ (bar);
    component.suspend?.();
    if (rotate && speed) {
      bar.setAttribute('autoplay', speed);
      bar.setAttribute('aria-live', 'polite');
      requestAnimationFrame(() => {
        component.current = 0;
        component.play?.();
      });
    } else {
      bar.removeAttribute('autoplay');
    }
  });
}

/* Toast */

/**
 * @param {string} code
 * @param {'active' | 'invalid'} kind
 * @param {string} message
 * @param {Config} config
 */
function showToast(code, kind, message, config) {
  const key = `${TOAST_KEY}${kind}:${code.toLowerCase()}`;
  const region = document.querySelector('[data-dl-toast-region]');
  if (!message || !region || storage.get(key)) return;
  storage.set(key, '1');

  const toast = document.createElement('div');
  toast.className = `dl-toast dl-toast--${kind}`;

  const text = document.createElement('p');
  text.className = 'dl-toast__text';
  text.textContent = message;

  const close = document.createElement('button');
  close.type = 'button';
  close.className = 'dl-toast__close';
  close.setAttribute('aria-label', config.closeLabel || 'Close');
  close.textContent = '×';

  toast.append(text, close);
  region.append(toast);

  let remaining = Math.max(1, Number(config.dismissSeconds) || 8) * 1000;
  let startedAt = 0;
  let timer = 0;

  const dismiss = () => {
    clearTimeout(timer);
    document.removeEventListener('keydown', onKeydown);
    const reduce = matchMedia('(prefers-reduced-motion: reduce)').matches;
    toast.classList.add('is-leaving');
    if (reduce) toast.remove();
    else setTimeout(() => toast.remove(), 250);
  };
  const start = () => {
    startedAt = Date.now();
    timer = window.setTimeout(dismiss, remaining);
  };
  const pause = () => {
    if (!timer) return;
    clearTimeout(timer);
    timer = 0;
    remaining = Math.max(1000, remaining - (Date.now() - startedAt));
  };
  /** @param {KeyboardEvent} event */
  function onKeydown(event) {
    if (event.key === 'Escape') dismiss();
  }

  close.addEventListener('click', dismiss);
  toast.addEventListener('mouseenter', pause);
  toast.addEventListener('mouseleave', () => !toast.matches(':focus-within') && start());
  toast.addEventListener('focusin', pause);
  toast.addEventListener('focusout', () => !toast.matches(':hover') && start());
  document.addEventListener('keydown', onKeydown);
  start();
}

/* Init */

async function init() {
  const config = readConfig();

  try {
    if (!config) return;

    // Testing aid: add ?dl_reset to a URL to forget the cached check and "toast already shown" flags.
    if (new URLSearchParams(location.search).has('dl_reset')) storage.clearAll();

    const code = detectCode();
    if (!code || code.length > 64) {
      console.info('[discount-landing] no discount code found');
      storage.set(CODE_KEY, '');
      return;
    }
    storage.set(CODE_KEY, code);

    const valid = await checkCode(code, config);
    const display = code.toUpperCase();
    console.info(`[discount-landing] code ${display}:`, valid === null ? 'could not be checked, showing nothing' : valid ? 'valid' : 'invalid or expired');

    if (valid === true) {
      watchCart(format(config.cart, display));
      if (config.replaceBar) replaceAnnouncementBar(config.bar.map((m) => format(m.trim(), display)).filter(Boolean));
      showToast(code, 'active', format(config.toast, display), config);
    } else if (valid === false) {
      showToast(code, 'invalid', format(config.invalid, display), config);
    }
  } finally {
    document.documentElement.classList.remove('dl-pending');
  }
}

init();
